import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, NativeSelect } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const EMPTY = { first_name: "", last_name: "", tax_code: "", birth_date: "", birth_place: "", sex: "M", email: "", phone: "",
  membership_number: "", membership_expiry: "", membership_status: "Valido", club: "", sector_id: "" };

export function AthleteForm({ open, onOpenChange, athlete, onSaved }) {
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [sectors, setSectors] = useState([]);
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(athlete ? { ...EMPTY, ...athlete } : { ...EMPTY, sector_id: user.sector_id || "" });
    setDup(null);
    api.get("/sectors").then((r) => setSectors(r.data));
  }, [open, athlete, user.sector_id]);

  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const checkDup = async () => {
    if (!f.tax_code && !f.membership_number) return;
    const { data } = await api.get("/athletes/check-duplicate", { params: { tax_code: f.tax_code || "-", membership_number: f.membership_number || "-" } });
    setDup(data.duplicate && data.duplicate.id !== athlete?.id ? data.duplicate : null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]]));
      const { data } = athlete ? await api.put(`/athletes/${athlete.id}`, payload) : await api.post("/athletes", payload);
      toast.success(athlete ? "Anagrafica aggiornata" : "Atleta inserito");
      onOpenChange(false);
      onSaved?.(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const input = (k, label, props = {}) => (
    <Field label={label}><Input value={f[k] || ""} onChange={set(k)} data-testid={`athlete-${k.replace(/_/g, "-")}-input`} {...props} /></Field>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="athlete-form-dialog">
        <DialogHeader><DialogTitle>{athlete ? "Modifica anagrafica" : "Nuovo atleta"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            {input("first_name", "Nome", { required: true })}
            {input("last_name", "Cognome", { required: true })}
            {input("tax_code", "Codice fiscale", { required: true, maxLength: 16, onBlur: checkDup, className: "uppercase font-mono" })}
            {input("membership_number", "Numero tessera MSP", { required: true, onBlur: checkDup, className: "font-mono" })}
            {input("birth_date", "Data di nascita", { type: "date", required: true })}
            {input("birth_place", "Luogo di nascita")}
            <Field label="Sesso">
              <NativeSelect value={f.sex} onChange={set("sex")} testid="athlete-sex-select"><option value="M">Maschile</option><option value="F">Femminile</option></NativeSelect>
            </Field>
            {input("club", "ASD/SSD di appartenenza")}
            {input("email", "Email", { type: "email" })}
            {input("phone", "Telefono")}
            {input("membership_expiry", "Scadenza tesseramento", { type: "date", required: true })}
            <Field label="Stato tesseramento">
              <NativeSelect value={f.membership_status} onChange={set("membership_status")} testid="athlete-membership-status-select">
                {["Valido", "Scaduto", "Sospeso", "Non rinnovato"].map((s) => <option key={s}>{s}</option>)}
              </NativeSelect>
            </Field>
            <Field label="Settore di riferimento">
              <NativeSelect value={f.sector_id || ""} onChange={set("sector_id")} disabled={user.role !== "admin"} required testid="athlete-sector-select">
                <option value="">Seleziona…</option>
                {sectors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </NativeSelect>
            </Field>
          </div>
          {dup && (
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="athlete-duplicate-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Anagrafica già esistente: {dup.last_name} {dup.first_name} (CF {dup.tax_code}, tessera {dup.membership_number}).
            </div>
          )}
          <DialogFooter><Button type="submit" disabled={busy || !!dup} className="bg-[#0F2042] hover:bg-[#1E3A8A]" data-testid="athlete-form-submit-btn">Salva anagrafica</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
